import React, {FC, useEffect} from 'react';
import styles from './OptimizationLoader.module.scss';
import {AIStarIcon, Button, Flex, Text, Title} from '@priority-software/priority-style-react';
import Lottie from 'react-lottie';
import animationData from './lottie/animation.json';
import {ModernTooltip} from '../../../../components/Common/Tooltip/ModernTooltip';
import {DataTestIds} from '../../constants';

const defaultOptions = {
    loop: true,
    autoplay: true,
    animationData: animationData,
    rendererSettings: {
        preserveAspectRatio: 'xMidYMid slice'
    }
};

export type OptimizationLoaderProps = {
    zIndex: number;
    title: string;
    description: string;
    cancelButtonText: string;
    cancelTooltip?: string;
    disableCancel?: boolean;
    onCloseProceed: () => void;
};

export const OptimizationLoader: FC<OptimizationLoaderProps> = ({
    zIndex,
    cancelButtonText,
    description,
    title,
    cancelTooltip,
    disableCancel = false,
    onCloseProceed
}) => {
    const cancelButton = (
        <Button
            dataTestId={DataTestIds.optimizationLoaderCancelButtonId}
            disabled={disableCancel}
            skin={'secondary'}
            size={'small'}
            onClick={() => onCloseProceed()}
        >
            {cancelButtonText}
        </Button>
    );

    useEffect(() => {
        const handler: EventListenerOrEventListenerObject = ev => {
            ev.preventDefault();
        };
        window.addEventListener('beforeunload', handler);

        return () => {
            window.removeEventListener('beforeunload', handler);
        };
    }, []);

    return (
        <div className={styles.wrapper} style={{zIndex}}>
            <div className={styles.loaderWrapper}>
                <div className={styles.loader}>
                    <Flex
                        direction={'row'}
                        componentClasses={styles.title}
                        justifyContent={'flex-start'}
                        alignItems={'center'}
                    >
                        <AIStarIcon />
                        <Title skin={'h2'} textColor={'primary-700'}>
                            {title}
                        </Title>
                    </Flex>
                    <Text
                        textColor={'primary-700'}
                        skin={'paragraph-1'}
                        componentClasses={styles.description}
                    >
                        {description}
                    </Text>
                    <Lottie
                        className={styles.lottie}
                        options={defaultOptions}
                        height={50}
                        width={200}
                    />
                    <Flex
                        componentClasses={styles.buttonWrapper}
                        alignItems={'flex-end'}
                        justifyContent={'flex-end'}
                    >
                        {cancelTooltip && disableCancel ? (
                            <ModernTooltip
                                tooltip={cancelTooltip}
                                maxWidth={400}
                                position={'top'}
                                tooltipOffset={8}
                                zIndex={zIndex + 1}
                            >
                                {cancelButton}
                            </ModernTooltip>
                        ) : (
                            cancelButton
                        )}
                    </Flex>
                </div>
            </div>
        </div>
    );
};
